import { useEffect, useState } from 'react'
import { Abschnitt, FeldZeile, feldKlasse, useFeld, useStellenDaten, type Speicherstelle } from '@/lib/speichern'
import { lookupAddresses, lookupContacts, lookupUsers } from '../api/calculations.api'
import type { CalcLookupAddress, CalcLookupContact, CalcLookupUser, Calculation } from '../types/calculation.types'
import { PartnerAuswahl } from './PartnerAuswahl'

/** ISO ↔ `datetime-local` in der Ortszeit des Browsers. */
function alsLokal(iso: unknown): string {
  if (!iso || typeof iso !== 'string') return ''
  const d = new Date(iso)
  const z = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}T${z(d.getHours())}:${z(d.getMinutes())}`
}

/** N1-15: Bezeichnung und Bauvorhaben tragen die Maske. */
const HERVOR = 'text-base font-semibold'

const ADRESSART: Record<CalcLookupAddress['type'], string> = { MAIN: 'Hauptadresse', BILLING: 'Rechnungsadresse', DELIVERY: 'Lieferadresse', OTHER: 'Adresse' }

type Adresse = { street: string; addition: string | null; zip: string; city: string; country: string }

function Anschrift({ a, leer = 'keine Hauptadresse im Stamm' }: { a: Adresse | null; leer?: string }) {
  if (!a) return <p className="mt-1 text-xs text-muted-foreground">{leer}</p>
  const zeilen = [a.street, a.addition, `${a.zip} ${a.city}`, a.country && a.country !== 'AT' ? a.country : null].filter(Boolean)
  return <p className="mt-1 whitespace-pre-line text-sm text-muted-foreground">{zeilen.join('\n')}</p>
}

/**
 * AP-37 4.1 — **Kalkulationsdaten** (eigener Reiter seit N1-12): Bezeichnung, Kunde oder
 * Interessent, Ansprechpartner, Bauvorhaben, Adresse, Abgabetermin, Bindefrist,
 * Kalkulant. Feldweise ohne Knopf (AP-36); eine festgeschriebene Fassung zeigt
 * die Felder gesperrt, wie Text.
 *
 * Kunde **oder** Interessent: Wer einen Kunden wählt, nimmt Interessent und
 * Ansprechpartner als Folgefelder mit (der Server leert den Freitext ohnehin).
 */
export function CalculationDataTab({ kalkulation, stelle }: { kalkulation: Calculation; stelle: Speicherstelle<unknown> }) {
  const d = useStellenDaten(stelle) as unknown as Calculation
  const gesperrt = !kalkulation.writable
  const [benutzer, setBenutzer] = useState<CalcLookupUser[]>([])
  const [kontakte, setKontakte] = useState<CalcLookupContact[]>([])
  const [adressen, setAdressen] = useState<CalcLookupAddress[]>([])
  const [kundeName, setKundeName] = useState<string | null>(kalkulation.client?.name ?? null)

  const name = useFeld(stelle, 'name', { label: 'Bezeichnung', pflicht: true, gesperrt })
  const beschreibung = useFeld(stelle, 'description', { label: 'Beschreibung', gesperrt })
  const kunde = useFeld<string | null>(stelle, 'clientId', {
    art: 'auswahl',
    label: 'Kunde',
    gesperrt,
    // Die Baustelle gehört zum alten Kunden (N1-20) — der Verweis geht mit, die Abschrift bleibt.
    folge: () => ({ contactPersonId: null, prospectName: null, prospectCity: null, siteAddressId: null }),
  })
  const kontakt = useFeld<string | null>(stelle, 'contactPersonId', { art: 'auswahl', label: 'Ansprechpartner', gesperrt })
  const interessent = useFeld(stelle, 'prospectName', { label: 'Interessent', gesperrt })
  const ort = useFeld(stelle, 'prospectCity', { label: 'Ort', gesperrt })
  const bauvorhaben = useFeld(stelle, 'siteName', { label: 'Bauvorhaben', gesperrt })
  const adresse = useFeld(stelle, 'siteAddress', { label: 'Adresse der Baustelle', gesperrt })
  const baustelle = useFeld<string | null>(stelle, 'siteAddressId', { art: 'auswahl', label: 'Baustelle', gesperrt })
  const verweis = (d.siteAddressId as string | null) ?? null
  const abgabe = useFeld<string | null>(stelle, 'dueAt', {
    label: 'Abgabetermin',
    gesperrt,
    lies: (x) => (x ? new Date(x).toISOString() : null),
    schreib: alsLokal,
  })
  const bindefrist = useFeld<string | null>(stelle, 'validUntil', { art: 'datum', label: 'Bindefrist', gesperrt })
  const kalkulant = useFeld<string>(stelle, 'ownerUserId', { art: 'auswahl', label: 'Kalkulant', pflicht: true, gesperrt })

  const clientId = (d.clientId as string | null) ?? null
  useEffect(() => {
    lookupUsers().then(setBenutzer).catch(() => setBenutzer([]))
  }, [])
  useEffect(() => {
    if (!clientId) return setKontakte([])
    lookupContacts(clientId).then(setKontakte).catch(() => setKontakte([]))
  }, [clientId])
  useEffect(() => {
    if (!clientId) return setAdressen([])
    lookupAddresses(clientId).then(setAdressen).catch(() => setAdressen([]))
  }, [clientId])

  return (
    <Abschnitt titel="Kalkulationsdaten">
      <div className="grid gap-x-8 gap-y-2 lg:grid-cols-2">
        <div className="space-y-2">
          <FeldZeile label="Bezeichnung" status={name}>
            <input className={`${feldKlasse} ${HERVOR}`} {...name.props} />
          </FeldZeile>
          {!kalkulation.isTemplate && (
            <>
              <FeldZeile label="Kunde" status={kunde}>
                {gesperrt ? (
                  <span className="block pt-1.5">{kundeName ?? '—'}</span>
                ) : (
                  <PartnerAuswahl
                    wert={clientId ? { id: clientId, name: kundeName ?? '' } : null}
                    klasse={feldKlasse}
                    onWahl={(p) => {
                      setKundeName(p?.name ?? null)
                      kunde.setze(p?.id ?? null)
                    }}
                  />
                )}
                {/* N1-15: Anschrift aus dem Stamm, nur zum Lesen — geändert wird sie beim Geschäftspartner. */}
                {clientId && <Anschrift a={d.clientAddress ?? null} />}
              </FeldZeile>
              {clientId ? (
                <FeldZeile label="Ansprechpartner" status={kontakt}>
                  <select className={feldKlasse} {...kontakt.props}>
                    <option value="">— niemand —</option>
                    {kontakte.map((k) => (
                      <option key={k.id} value={k.id}>
                        {[k.firstName, k.lastName].filter(Boolean).join(' ')}
                      </option>
                    ))}
                  </select>
                </FeldZeile>
              ) : (
                <>
                  <FeldZeile label="Interessent" status={interessent} hilfe="Wer noch nicht im Stamm steht — beim Angebot wird er angelegt.">
                    <input className={feldKlasse} {...interessent.props} />
                  </FeldZeile>
                  <FeldZeile label="Ort" status={ort}>
                    <input className={feldKlasse} {...ort.props} />
                  </FeldZeile>
                </>
              )}
            </>
          )}
          <FeldZeile label="Beschreibung" status={beschreibung}>
            <textarea className={feldKlasse} rows={2} {...beschreibung.props} />
          </FeldZeile>
        </div>
        <div className="space-y-2">
          {!kalkulation.isTemplate && (
            <>
              <FeldZeile label="Bauvorhaben" status={bauvorhaben}>
                <input className={`${feldKlasse} ${HERVOR}`} {...bauvorhaben.props} />
              </FeldZeile>
              {/* N1-20: Baustelle als Verweis auf eine Kundenadresse (geht ans Projekt mit) — oder frei eingegeben. */}
              <FeldZeile label="Adresse der Baustelle" status={verweis ? baustelle : adresse}>
                {clientId && !gesperrt && (adressen.length > 0 || verweis) && (
                  <select className={`${feldKlasse} mb-1`} aria-label="Baustelle aus den Kundenadressen" {...baustelle.props}>
                    <option value="">eigene Adresse — frei eingeben</option>
                    {adressen.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.type === 'OTHER' && a.label ? a.label : ADRESSART[a.type]} — {a.street}, {a.zip} {a.city}
                      </option>
                    ))}
                  </select>
                )}
                {verweis ? (
                  <Anschrift a={d.siteAddressRef ?? null} leer="Adresse im Stamm nicht mehr vorhanden" />
                ) : (
                  <textarea className={feldKlasse} rows={2} placeholder={'Straße\nPLZ Ort'} {...adresse.props} />
                )}
              </FeldZeile>
              <FeldZeile label="Abgabetermin" status={abgabe} hilfe="Mit Uhrzeit — Ausschreibungen enden oft um 10:00.">
                <input type="datetime-local" className={feldKlasse} {...abgabe.props} />
              </FeldZeile>
              <FeldZeile label="Bindefrist" status={bindefrist}>
                <input type="date" className={feldKlasse} {...bindefrist.props} />
              </FeldZeile>
            </>
          )}
          <FeldZeile label="Kalkulant" status={kalkulant}>
            <select className={feldKlasse} {...kalkulant.props}>
              {benutzer.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </FeldZeile>
          <FeldZeile label="Zuschlagsschema">
            <span className="block pt-1.5 text-muted-foreground">
              {kalkulation.schemeName ?? '—'} · {kalkulation.laborMode === 'BILLING_RATE' ? 'Verrechnungssatz' : 'Kostensatz + Aufschläge'}
            </span>
          </FeldZeile>
        </div>
      </div>
    </Abschnitt>
  )
}
